/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

/**
 * How a saved note is drawn in the thread.
 *
 * **A note is stored as Markdown.** Bold is `**x**`, italic `*x*`, strike `~~x~~`,
 * and a list is lines starting `- ` or `1. `. The composer is a live editor that
 * writes that text down (`noteMarkdown.ts`) and this file reads it back for the
 * thread. Nothing about the stored text is HTML, so there is no markup to
 * sanitise and nothing for a note to inject. Every other channel — push, Teams,
 * email, the bell — is given the same words with the markers removed by
 * `server/richText.js`.
 *
 * **That file and this one are a pair.** It strips the grammar below; this draws
 * it. A marker one of them understands and the other does not is a note that
 * looks one way here and another on somebody's phone, which is why both are
 * tested against the same table of cases.
 */

/* ------------------------------------------------------------------ *
 * Reading: text -> what to draw
 * ------------------------------------------------------------------ */

interface Inline {
  text: string;
  bold?: boolean;
  italic?: boolean;
  strike?: boolean;
}

/**
 * One alternation so the **earliest** marker wins and `**` beats `*` at the same
 * position. An opening marker must be followed by something that is not a space
 * and a closing one must follow something that is not — so `2 * 3 * 4` is
 * arithmetic, not italics. No `s` flag: a marker never spans a line.
 */
const INLINE = /\*\*(?=\S)(.+?)(?<=\S)\*\*(?!\*)|~~(?=\S)(.+?)(?<=\S)~~|\*(?=[^\s*])(.+?)(?<=[^\s*])\*(?!\*)/;

/**
 * A line, as runs of text that each carry their own style.
 *
 * Recurses into what a marker wraps, so `**bold *and italic***` is one bold run
 * containing an italic one rather than a literal asterisk. An unmatched marker
 * is left exactly as typed.
 */
export function parseInline(text: string, style: Omit<Inline, 'text'> = {}): Inline[] {
  const out: Inline[] = [];
  let rest = text;
  for (;;) {
    const m = INLINE.exec(rest);
    if (!m) {
      if (rest) out.push({ text: rest, ...style });
      return out;
    }
    if (m.index > 0) out.push({ text: rest.slice(0, m.index), ...style });
    if (m[1] !== undefined) out.push(...parseInline(m[1], { ...style, bold: true }));
    else if (m[2] !== undefined) out.push(...parseInline(m[2], { ...style, strike: true }));
    else if (m[3] !== undefined) out.push(...parseInline(m[3], { ...style, italic: true }));
    rest = rest.slice(m.index + m[0].length);
  }
}

type Block =
  | { kind: 'text'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[]; start: number };

const BULLET = /^- (.*)$/;
const NUMBER = /^(\d+)\. (.*)$/;

/**
 * Lines grouped into paragraphs and lists.
 *
 * Consecutive lines of one kind are one list. Text lines are kept together with
 * their newlines, because the thread is drawn `whitespace-pre-wrap` — a note
 * typed as two lines must still be two lines. A numbered list opens at the
 * number it was typed with, so "2." on its own line is not silently renumbered.
 */
export function parseBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  for (const line of text.split('\n')) {
    const bullet = BULLET.exec(line);
    const number = NUMBER.exec(line);
    const last = blocks[blocks.length - 1];

    if (bullet) {
      if (last && last.kind === 'ul') last.items.push(bullet[1] ?? '');
      else blocks.push({ kind: 'ul', items: [bullet[1] ?? ''] });
    } else if (number) {
      if (last && last.kind === 'ol') last.items.push(number[2] ?? '');
      else blocks.push({ kind: 'ol', items: [number[2] ?? ''], start: Number(number[1]) });
    } else if (last && last.kind === 'text') {
      last.text += `\n${line}`;
    } else {
      blocks.push({ kind: 'text', text: line });
    }
  }
  return blocks;
}
